import React, { useRef } from 'react';
import { Layers, ChevronLeft, ChevronRight } from 'lucide-react';
import { CuratedCollection, MediaItem } from '../types';
import { CollectionCard } from './CollectionCard';

interface CollectionsRowProps {
  collections: CuratedCollection[];
  catalog: MediaItem[];
  onSelectCollection: (colId: string) => void;
  onViewAll?: () => void;
}

export const CollectionsRow: React.FC<CollectionsRowProps> = ({
  collections,
  catalog,
  onSelectCollection,
  onViewAll
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    const scrollAmount = clientWidth * 0.75;
    scrollRef.current.scrollTo({
      left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
      behavior: 'smooth'
    });
  };

  if (!collections || collections.length === 0) return null;

  return (
    <div id="row-collections-preview" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display font-black text-xl sm:text-2xl text-white tracking-wide">
              Col·leccions
            </h2>
            <p className="text-xs text-slate-400">
              Explora els teus catàlegs temàtics i sagues
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onViewAll && (
            <button
              type="button"
              data-focusable="true"
              onClick={onViewAll}
              className="text-xs font-semibold text-sky-400 hover:text-sky-300 transition-colors px-2.5 py-1 rounded-lg hover:bg-sky-500/10 focus:ring-2 focus:ring-sky-400 outline-none"
            >
              Veure-les totes ({collections.length})
            </button>
          )}

          {/* Navigation Arrows for large screens */}
          <div className="hidden sm:flex items-center gap-1">
            <button
              type="button"
              data-focusable="true"
              onClick={() => handleScroll('left')}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 transition-colors"
              title="Desplaçar a l'esquerra"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-focusable="true"
              onClick={() => handleScroll('right')}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 transition-colors"
              title="Desplaçar a la dreta"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal List of Collections - ONE item per collection */}
      <div 
        ref={scrollRef}
        className="flex items-stretch gap-4 overflow-x-auto pb-4 pt-1 no-scrollbar scroll-smooth"
      >
        {collections.map(col => (
          <div key={col.id} className="w-44 sm:w-52 md:w-60 shrink-0">
            <CollectionCard
              collection={col}
              catalog={catalog}
              onClick={() => onSelectCollection(col.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
